<script setup lang="ts">
import {
  $approveLineItem,
  $lineItem,
  $lineItemAnnotationCreate,
  $lineItemAnnotationDelete,
  $lineItemAnnotationList,
  $lineItemAnnotationUpdate,
  $lineItemAnnotationCommentCreate,
  $getNextPreviousLineItem,
  $getLineItemWfPreviousStatuses,
} from '@/api/brandings'
import VuePdfEmbed from 'vue-pdf-embed'
import { can } from '@layouts/plugins/casl'
import ability from '@/plugins/casl/ability'
import { VDataTableVirtual } from 'vuetify/labs/VDataTable'
import { useSnackbarStore } from '@/store/snackbar'
import { useUserStore } from '@/store/user'
import type { ComponentPublicInstance } from 'vue'
import {
  onWaitingRoomAutoSyncRefresh
} from '@/helpers/events/waitingRoomAutoSync'
import { useWaitingRoomAutoSyncStore } from '@/store/waitingRoomAutoSync'
import {
  downloadAllFilesUsingLinks,
  downloadFileUsingLink,
  downloadFromS3,
  downloadFromS3MultiFolders
} from '@/helpers/utils/downloadAllFilesUsingLinks'
import { getPreferredPdfAiLink } from '@/helpers/utils/dropboxLinks'
import { getDownloadLinkMetadata } from '@/helpers/utils/downloadLinkMetadata'

interface IProps {
  projectId?: string | number,
  workOrderId?: string | number,
  lineItemId?: string | number,
  isGlobalSearch?: boolean,
  searchQuery?: string,
  searchByQuery?: string,
  isReviewDialog?: boolean,
}

const props = withDefaults(defineProps<IProps>(), {
  projectId: '',
  workOrderId: '',
  lineItemId: '',
  isGlobalSearch: false,
  searchQuery: '',
  searchByQuery: '',
  isReviewDialog: false,
})

interface Emit {
  (e: 'onApprovalAction'): void
  (e: 'uploaded'): void
}

const emit = defineEmits<Emit>()

type S3Folder =
  | 'ARTWORK'
  | 'PREPRESS_PROOF'
  | 'INSTALLATION'
  | 'SCOPE'

const snackbarStore = useSnackbarStore()
const userStore = useUserStore()
const waitingRoomAutoSyncStore = useWaitingRoomAutoSyncStore()

const router = useRouter()
const route = useRoute()

const artworksBlock = ref<ComponentPublicInstance | null>(null)  // DOM element

const lineItem = ref<any>({})
const annotations = ref<any[]>([])
const showAnnotationsSidebar = ref<boolean>(false)
const showAnnotationCommentDialog = ref<boolean>(false)
const parentAnnotation = ref<any | null>(null)
const artworkFileLoading = ref<boolean>(true)
const scopeLoading = ref<boolean>(true)
const prePressProofLoading = ref<boolean>(true)
const artworkFilePageCount = ref<number>(1)
const scopePageCount = ref<number>(1)
const prePressProofPageCount = ref<number>(1)
const artworkFileCurrentPage = ref<number>(1)
const scopeCurrentPage = ref<number>(1)
const prePressProofCurrentPage = ref<number>(1)
const isMounted = ref<boolean>(false)
const pdfScale = ref<number>(100)
const isMoving = ref<boolean>(false)
const artworkFileMapPos = ref<{ left: number, top: number, x: number, y: number }>({ left: 0, top: 0, x: 0, y: 0 })
const scopeMapPos = ref<{ left: number, top: number, x: number, y: number }>({ left: 0, top: 0, x: 0, y: 0 })
const prePressProofMapPos = ref<{ left: number, top: number, x: number, y: number }>({ left: 0, top: 0, x: 0, y: 0 })
const isMapCenter = ref<boolean>(false)
const isLineItemLoaded = ref<boolean>(false)
const artworkComparisonSelect = ref<0 | 1 | 2>(0)
const artworkComparisons = ref([
  { value: 0, label: 'Artwork File - PrePress Proof' },
  { value: 1, label: 'Artwork File - Scope PDF' },
  { value: 2, label: 'PrePress Proof - Scope PDF' }
])
const lineItemStatuses = ref([])

const lineItemLoading = ref<boolean>(false)
const showProjectFilesUploadDialog = ref(false)

const selectedUploadItemIds = computed<number[]>(() => {
  const id = Number(props.lineItemId)
  return Number.isInteger(id) && id > 0 ? [id] : []
})

const isFileUploadSupported = computed(() => {
  return selectedUploadItemIds.value.length === 1
    && Number(lineItem.value?.id) === selectedUploadItemIds.value[0]
    && lineItem.value?.campaign_type === 2
})

const isFileUploadReady = computed(() => {
  return isFileUploadSupported.value && !lineItemLoading.value
})

const selectedUploadExpectedItems = computed(() => {
  return selectedUploadItemIds.value.map(id => ({
    id,
    name: lineItem.value?.file_name || '',
    statusName: lineItem.value?.item_status_info?.line_item_status_name || null,
  }))
})

const waitingRoomProjectOptions = computed(() => {
  const id = Number(lineItem.value?.campaign)
  return Number.isInteger(id) && id > 0
    ? [{ id, name: lineItem.value?.campaign_name || `Project #${id}` }]
    : []
})

const openFilesUploadDialog = () => {
  if (isFileUploadReady.value) {
    showProjectFilesUploadDialog.value = true
  }
}

const closeFilesUploadDialog = () => {
  showProjectFilesUploadDialog.value = false
}

watch(() => props.lineItemId, closeFilesUploadDialog)

watch(isFileUploadSupported, isSupported => {
  if (!isSupported) {
    closeFilesUploadDialog()
  }
})

// loading states
const downloadAllFilesLoading = ref<boolean>(false)
const singleDownloadLoading = ref({}) as any // map url->boolean

const approveBtnLoading = ref<boolean>(false)
const rejectBtnLoading = ref<boolean>(false)

const previousStatusList = ref<any[]>([])
const previousStatusListLoading = ref<boolean>(false)

const showGlobalChatDialog = ref<boolean>(false)
const showLineItemStatusHistoryDialog = ref<boolean>(false)

// Item Ext Ref Num Dialog
const showItemExtRefNumDialog = ref(false)

const isActiveCampaign = computed<boolean>(() => {
  return lineItem.value?.is_active_campaign || true
})

const isLinkCreatedUser = computed<boolean>(() => {
  return userStore.isLinkCreatedUser
})

const isCanAddAnnotation = computed<boolean>(() => {
  return !isLinkCreatedUser.value && ability.can('add', 'brandingartworkannot') && isActiveCampaign.value
})

// Computed property to check if all URLs are empty
const isDisabledDownloadAllLink = computed<boolean>(() => {
  const urls = [
    isArtworkInS3.value,
    isPrePressProofInS3.value,
    isScopeInS3.value
  ]

  // Check if all URLs are empty or invalid
  return !urls.some(url => url) // Returns true if all URLs are empty
})

const isShownAnyAnnotationMenu = computed<boolean>(() => {
  return !!annotations.value
    .filter((annot: any) => annot.page === artworkFileCurrentPage.value && !!annot.isShownAnnotationMenu).length
})

const isDisabledApproveBtn = computed(() => {
  return !!annotations.value.filter((annot: any) => !annot.is_resolved).length
})

const getResponsibleDisplayName = (responsible: any) => {
  const fullName = [responsible?.first_name, responsible?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()

  return fullName || responsible?.email || '-'
}

watch(artworkComparisonSelect, () => {
  pdfScale.value = 100
})

const getLineItem = async () => {
  lineItemLoading.value = true
  try {
    const res = await $lineItem(props.lineItemId)
    lineItem.value = res.data

    // show PDFs with actual url(if exist)
    if (artworkFileUrl.value && prePressProofUrl.value) {
      artworkComparisonSelect.value = 0
    }
    else if (artworkFileUrl.value && scopeUrl.value) {
      artworkComparisonSelect.value = 1
    }
    else if (prePressProofUrl.value && scopeUrl.value) {
      artworkComparisonSelect.value = 2
    }
    else if (artworkFileUrl.value || prePressProofUrl.value) {
      artworkComparisonSelect.value = 0
    }
    else if (scopeUrl.value) {
      artworkComparisonSelect.value = 1
    }
  } catch (e: any) {
    const mes = e?.response?.data ? e?.response?.data : e?.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    lineItemLoading.value = false
  }
}

const onFilesUploaded = async () => {
  await getLineItem()
  emit('uploaded')
}

const unsubscribeWaitingRoomAutoSyncRefresh = onWaitingRoomAutoSyncRefresh(result => {
  const isCurrentLineItemAffected = result.affected_targets.some(target => (
    target.target_type === 'line_item' && String(target.target_id) === String(props.lineItemId)
  ))

  if (!props.isReviewDialog && isCurrentLineItemAffected) {
    void getLineItem()
  }
})

const approveLineItem = async (isApprove = true, rejectStatusId = null) => {
  if (!props.lineItemId) return

  if (isApprove) {
    approveBtnLoading.value = true
  } else {
    rejectBtnLoading.value = true
  }

  try {
    waitingRoomAutoSyncStore.handleResponse(await $approveLineItem(props.lineItemId?.toString(), {
      is_approve: isApprove,
      reject_status_id: rejectStatusId ? rejectStatusId : undefined
    }))
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Item updated successfully!'
    })
    if (!isLinkCreatedUser.value) {
      await getLineItem()
    }

    emit('onApprovalAction')
  } catch (e: any) {
    const mes = e?.response?.data?.message ? e?.response?.data?.message :
      (e?.response?.data ? JSON.stringify(e?.response?.data) : e?.message)
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    if (isApprove) {
      approveBtnLoading.value = false
    } else {
      rejectBtnLoading.value = false
    }
  }
}

const rejectLineItem = async (rejectStatusId = null) => {
  await approveLineItem(false, rejectStatusId)
}

const getLineItemWfPreviousStatuses = async () => {
  if (!props.lineItemId) return

  previousStatusListLoading.value = true
  try {
    const res = await $getLineItemWfPreviousStatuses(props.lineItemId?.toString())
    previousStatusList.value = res.data
  } catch (e: any) {
    const mes = e.response.data ? e.response.data : e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    previousStatusListLoading.value = false
  }
}

const handleRejectMenuOpen = (isOpen: boolean) => {
  if (isOpen) {
    getLineItemWfPreviousStatuses()
  }
}

const setHover = (annotation: any, status: boolean) => {
  annotation['hover'] = status
}

const hideAnnotationMenus = () => {
  annotations.value = annotations.value.map(i => ({ ...i, isShownAnnotationMenu: false }) )
}

const getAnnotations = async () => {
  try {
    const res = await $lineItemAnnotationList({
      line_item: props.lineItemId
    })
    annotations.value = res.data.results
  } catch (e: any) {
    console.log(e)
  }
}

const createAnnotation = async (event: MouseEvent) => {
  const textLayer = document.getElementsByClassName('textLayer')[0] as HTMLElement
  if (
    event.target === textLayer
    && !(event.target instanceof HTMLElement && event.target.hasAttribute('annot'))
    && !isShownAnyAnnotationMenu.value
    && isCanAddAnnotation.value
  ) {
    const topPerc = (event.offsetY - 12) / textLayer.offsetHeight * 100
    const leftPerc = (event.offsetX - 12) / textLayer.offsetWidth * 100
    const payload = {
      line_item: props.lineItemId,
      top: topPerc,
      left: leftPerc,
      page: artworkFileCurrentPage.value
    }
    try {
      const res = await $lineItemAnnotationCreate(payload)
      annotations.value.push(res.data)
    } catch (e: any) {
      console.log(e)
    }
  } else if (isShownAnyAnnotationMenu.value) {
    hideAnnotationMenus()
  }
}

const updateAnnotation = async (annotation: any) => {
  if (isLinkCreatedUser.value) return

  const ref = 'pop' + annotation.id
  try {
    await $lineItemAnnotationUpdate(annotation.id, annotation)
    hideAnnotationMenus()
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Annotation saved successfully!'
    })
  } catch (e: any) {
    console.log(e)
  }
}

const deleteAnnotation = async (id: number, key: number) => {
  if (isLinkCreatedUser.value) return

  try {
    await $lineItemAnnotationDelete(id)
    annotations.value = annotations.value.filter(ann => ann.id !== id)
    hideAnnotationMenus()
     snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Annotation deleted successfully!'
    })

  } catch (e: any) {
    console.log(e)
  }
}

const createComment = async (comment: string) => {
  if (isLinkCreatedUser.value) return

  const payload = {
    line_item: props.lineItemId,
    content: comment,
    annotation: parentAnnotation.value.id
  }
  try {
    await $lineItemAnnotationCommentCreate(payload)
    showAnnotationCommentDialog.value = false
    parentAnnotation.value = null
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Comment saved!'
    })
    await getAnnotations()
  } catch (e: any) {
    console.log(e)
  }
}

const previousLineItem = async () => {
  if (!props.lineItemId) return

  const payload = {
    is_previous: true,
    search: props.searchQuery,
    search_by: props.searchByQuery
  } as { is_previous: boolean, search: string, search_by: string, work_order_id?: string | number | null | undefined }
  if (!props.isGlobalSearch) {
    payload.work_order_id = lineItem.value.work_order
  }
  try {
    const res = await $getNextPreviousLineItem(props.lineItemId?.toString(), payload)
    const requestStatus = res.status
    const nextLineItem = res.data

    await redirectToNextPreviousLineItem(requestStatus, nextLineItem, true)
  } catch (e: any) {
    console.log(e)
  }
}

const nextLineItem = async () => {
  if (!props.lineItemId) return

  const payload = {
    search: props.searchQuery,
    search_by: props.searchByQuery
  } as { search: string, search_by: string, work_order_id?: string | number | null | undefined }
  if (!props.isGlobalSearch) {
    payload.work_order_id = lineItem.value?.work_order
  }
  try {
    const res = await $getNextPreviousLineItem(props.lineItemId?.toString(), payload)
    const requestStatus = res.status
    const nextLineItem = res.data

    await redirectToNextPreviousLineItem(requestStatus, nextLineItem)
  } catch (e: any) {
    console.log(e)
  }
}

const redirectToNextPreviousLineItem = async (requestStatus: any, nextLineItem: any, isPrevious = false as boolean) => {
  // If next line item request status - 204 'No Content' -> redirect to line item list page
  if (requestStatus === 204) {
    const snackBarText = `${isPrevious ? 'Previous' : 'Next'} line item not found`
    snackbarStore.showMessage({
      color: 'warning',
      icon: 'mdi-alert-circle',
      title: 'Warning',
      text: snackBarText
    })
  } else {
    if (props.isGlobalSearch) {
      await router.push({
        path: `/projects/branding/global-search-line-items/${nextLineItem.id}`,
        query: { search: props.searchQuery, 'search-by': props.searchByQuery } })
    } else {
      await router.push({
        path: `/projects/branding/${props.projectId}/work-orders/${props.workOrderId}/line-items/${nextLineItem.id}`,
        query: { search: props.searchQuery, 'search-by': props.searchByQuery }
      })
    }
  }
}

const onMoveMapStart = (e: MouseEvent, mapPos: any, elementId: string) => {
  // On mouse left button (primary) - move PDF
  if (e.button === 0) {
    const el = document.getElementById(elementId) as HTMLElement
    isMoving.value = true
    mapPos.left = el.scrollLeft
    mapPos.top = el.scrollTop
    mapPos.x = e.clientX
    mapPos.y = e.clientY
  }
}

const onMoveMap = (e: MouseEvent, isCenter: boolean, mapPos: any, elementId: string) => {
  if (isCenter) {
    setTimeout(() => {
      const containerIds = ['artwork-file-pdf-container-id', 'scope-pdf-container-id',
        'prepress-proof-pdf-container-id']
      containerIds.forEach(containerId => {
        const container = document.getElementById(containerId) as HTMLElement
        container.scrollTop = container.scrollHeight / 2 - container.clientHeight / 2
        container.scrollLeft = container.scrollWidth / 2 - container.clientWidth / 2
      })
    }, 0)
  } else {
    const el = document.getElementById(elementId) as HTMLElement
    const dx = e.clientX - mapPos.x
    const dy = e.clientY - mapPos.y

    el.scrollTop = mapPos.top - dy
    el.scrollLeft = mapPos.left - dx
  }
}

const getS3FileByType = (item: any, artworkType: number) => {
  if (!item) return null

  const folderByType: Record<number, string> = {
    0: 'artwork',
    3: 'prepress_proof',
    4: 'scope',
    1: 'installation_photos',
  }

  const folder = folderByType[artworkType]
  if (!folder) return null

  const fileObject = item?.[`${folder}_file`] || null
  const fileLinks = Array.isArray(item?.[`${folder}_file_links`]) ? item[`${folder}_file_links`] : []
  const urls = fileLinks.map((link: any) => link?.url).filter((url: any): url is string => !!url)
  const preferredUrl = urls.length > 1 ? (getPreferredPdfAiLink(urls) || urls[0]) : (urls[0] || null)

  if (!fileObject && !preferredUrl && !item?.[`${folder}_file_updated_at`]) return null

  return {
    type: artworkType,
    url: preferredUrl,
    filename: fileObject?.file_name || null,
    file_modified_date: item?.[`${folder}_file_updated_at`] || null,
    is_in_s3: !!fileObject,
  }
}

const getItemFileDisplay = (item: any, artworkType: number) => {
  const art = getS3FileByType(item, artworkType)
  if (!art) return null
  return art.filename ?? art.file_modified_date ?? null
}

// computed urls for each link in the template
const artworkFileUrl = computed(() => {
  const artworkLinks = Array.isArray(lineItem.value?.artwork_file_links)
    ? lineItem.value.artwork_file_links.map((i: any) => i?.url).filter((url: any): url is string => !!url)
    : []

  // For multiple URLs, try to find a PDF
  return getPreferredPdfAiLink(artworkLinks)
})
const prePressProofUrl = computed(() => {
  const prePressProofLinks = Array.isArray(lineItem.value?.prepress_proof_file_links)
    ? lineItem.value.prepress_proof_file_links.map((i: any) => i?.url).filter((url: any): url is string => !!url)
    : []

  // For multiple URLs, try to find a PDF
  return getPreferredPdfAiLink(prePressProofLinks)
})
const scopeUrl = computed(() => {
  const scopeLinks = Array.isArray(lineItem.value?.scope_file_links)
    ? lineItem.value.scope_file_links.map((i: any) => i?.url).filter((url: any): url is string => !!url)
    : []

  // For multiple URLs, try to find a PDF
  return getPreferredPdfAiLink(scopeLinks)
})
const prodAiUrl = computed(() => {
  return ''
})
const artworkDownloadInfo = computed(() => getDownloadLinkMetadata(
  lineItem.value?.artwork_file_links,
  'Artwork',
))
const prePressProofDownloadInfo = computed(() => getDownloadLinkMetadata(
  lineItem.value?.prepress_proof_file_links,
  'Prepress Proof',
))
const scopeDownloadInfo = computed(() => getDownloadLinkMetadata(
  lineItem.value?.scope_file_links,
  'Scope',
))
const allImagesDownloadInfo = computed(() => {
  const groups = [
    artworkDownloadInfo.value,
    prePressProofDownloadInfo.value,
    scopeDownloadInfo.value,
  ].filter(group => group.hasFiles)
  const count = groups.reduce((total, group) => total + group.count, 0)

  return {
    count,
    hasFiles: count > 0,
    label: count ? `Download (${count})` : 'Download',
    tooltipLines: groups.map(group => group.tooltipLine),
  }
})
const isArtworkInS3 = computed(() => {
  return !!getS3FileByType(lineItem.value, 0)?.is_in_s3
})
const isPrePressProofInS3 = computed(() => {
  return !!getS3FileByType(lineItem.value, 3)?.is_in_s3
})
const isScopeInS3 = computed(() => {
  return !!getS3FileByType(lineItem.value, 4)?.is_in_s3
})

const brandingFileIdentity = computed(() => {
  const fileName = (lineItem.value?.file_name || '').toString().trim()
  if (fileName) {
    const dotIndex = fileName.lastIndexOf('.')
    return dotIndex > 0 ? fileName.slice(0, dotIndex).trim() : fileName
  }

  return (lineItem.value?.brand_book_code || '').toString().trim()
})

watch(artworkFileUrl, url => {
  artworkFileLoading.value = !!url
}, { immediate: true })

watch(prePressProofUrl, url => {
  prePressProofLoading.value = !!url
}, { immediate: true })

watch(scopeUrl, url => {
  scopeLoading.value = !!url
}, { immediate: true })

// single file download handler
const downloadSingleFile = async (link: string | null, fileType?: 'artwork' | 'prepress_proof' | 'scope') => {
  // S3-preferred path
  if (
      (fileType === 'prepress_proof' && isPrePressProofInS3.value) ||
      (fileType === 'artwork' && isArtworkInS3.value) ||
      (fileType === 'scope' && isScopeInS3.value)
  ) {
    if (link) singleDownloadLoading.value[link] = true
    try {
      const result = await downloadFromS3(
        lineItem.value.campaign,
        brandingFileIdentity.value,
        fileType,
      )
      if (result.status === 'complete') {
        snackbarStore.showMessage({ color: 'success', icon: 'mdi-checkbox-marked-circle', title: 'Download Complete', text: 'File downloaded successfully!' })
      } else {
        snackbarStore.showMessage({ color: 'error', icon: 'mdi-alert-circle', title: 'Download Failed', text: 'Failed to download' })
      }
    } catch (err: any) {
      snackbarStore.showMessage({ color: 'error', icon: 'mdi-alert-circle', title: 'Download Failed', text: 'Unexpected error while downloading' })
    } finally {
      if (link) singleDownloadLoading.value[link] = false
    }
    return
  }

  if (!link) {
    snackbarStore.showMessage({
      color: 'warning',
      icon: 'mdi-alert-circle',
      title: 'Warning',
      text: 'No link available'
    })
    return
  }

  singleDownloadLoading.value[link] = true

  try {
    const result = await downloadFileUsingLink(link)
    if (result.status === 'success') {
      snackbarStore.showMessage({
        color: 'success',
        icon: 'mdi-checkbox-marked-circle',
        title: 'Download Complete',
        text: `Downloaded ${result.filename}`
      })
    } else {
      snackbarStore.showMessage({
        color: 'error',
        icon: 'mdi-alert-circle',
        title: 'Download Failed',
        text: `Failed: ${result.error ?? 'Unknown error'}`
      })
    }
  } catch (err: any) {
    console.log('downloadSingleFile error:', err)
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Download Failed',
      text: 'Unexpected error while downloading'
    })
  } finally {
    singleDownloadLoading.value[link] = false
  }
}

// download all files handler — collects urls and download
const downloadAllFiles = async () => {
  downloadAllFilesLoading.value = true

  // S3-preferred path for artwork
  if (!isDisabledDownloadAllLink.value) {
    try {
      const folders: S3Folder[] = []

      if (isArtworkInS3.value)
        folders.push('ARTWORK')

      if (isPrePressProofInS3.value)
        folders.push('PREPRESS_PROOF')

      if (isScopeInS3.value)
        folders.push('SCOPE')

      const result = await downloadFromS3MultiFolders(
        lineItem.value.campaign,
        {
          code: brandingFileIdentity.value,
          folders: folders
        }
      )
      if (result?.status === 'complete') {
        snackbarStore.showMessage({ color: 'success', icon: 'mdi-checkbox-marked-circle', title: 'Download Complete', text: 'File(s) downloaded successfully!' })
      } else {
        snackbarStore.showMessage({ color: 'error', icon: 'mdi-alert-circle', title: 'Download Failed', text: 'Failed to download' })
      }
    } catch (e: any) {
      snackbarStore.showMessage({ color: 'error', icon: 'mdi-alert-circle', title: 'Download Failed', text: 'Unexpected error' })
    } finally {
      downloadAllFilesLoading.value = false
    }
    return
  }
}

const initReviewBlock = () => {
  getLineItem()
  getAnnotations()

  // set max height to Artworks Block(with PDFs)
  if (artworksBlock.value) {
    const windowHeight = window.innerHeight
    const elementRect = artworksBlock.value.$el.getBoundingClientRect()
    // Where 50(title height + padding) and 72(48 - footer height + 24 - layout padding)
    const blockPadding = props.isReviewDialog ? 50 : 72
    const remainingHeight = windowHeight - elementRect.top - blockPadding  //
    artworksBlock.value.$el.style.maxHeight = remainingHeight + 'px'
  }
}

// Expose the function to parent components
defineExpose({
  initReviewBlock,
  isFileUploadSupported,
  isFileUploadReady,
  openFilesUploadDialog,
  closeFilesUploadDialog,
})

onMounted(() => {
  if (!props.isReviewDialog) {
    initReviewBlock()
  }
})

onBeforeUnmount(() => {
  unsubscribeWaitingRoomAutoSyncRefresh()
})

const getLineItemListLink = () => {
  if (props.isGlobalSearch) {
    return {
      path: '/projects/branding/global-search-line-items',
      query: { search: props.searchQuery, 'search-by': props.searchByQuery }
    }
  } else {
    return {
      path: `/projects/branding/${props.projectId}/work-orders/${props.workOrderId}`,
      query: { search: props.searchQuery, 'search-by': props.searchByQuery }
    }
  }
}
</script>

<template>
  <div>
    <ProjectFilesUploadDialog
      v-if="isFileUploadSupported"
      v-model="showProjectFilesUploadDialog"
      :project-id="lineItem?.campaign || null"
      :selected-item-ids="selectedUploadItemIds"
      :expected-items="selectedUploadExpectedItems"
      :project-type-id="2"
      :waiting-room-project-options="waitingRoomProjectOptions"
      @uploaded="onFilesUploaded"
    />
    <AnnotationsSidebar
      v-model="showAnnotationsSidebar"
      v-model:annotations="annotations"
      :current-page="artworkFileCurrentPage"
      :artwork-select="artworkComparisonSelect"
      :line-item="lineItem"
      :readonly="isLinkCreatedUser"
      @onSetHover="setHover($event.annotation, $event.status)"
      @onComment="parentAnnotation = $event; showAnnotationCommentDialog = true"
    />
    <AnnotationCommentDialog
      v-model="showAnnotationCommentDialog"
      :parent-annotation="parentAnnotation"
      @onSubmit="createComment($event)"
    />
    <GlobalChatDialog
      v-if="!isLinkCreatedUser"
      v-model="showGlobalChatDialog"
      :item-id="lineItemId"
      item-type="branding"
    />
    <ItemStatusHistoryDialog
      v-if="!isLinkCreatedUser"
      v-model="showLineItemStatusHistoryDialog"
      :item-id="lineItemId"
      item-type="branding"
      :code="lineItem?.brand_book_code || ''"
    />
    <UpdateItemExtRefNumDialog
      v-if="!isLinkCreatedUser"
      v-model="showItemExtRefNumDialog"
      :campaign-type="'branding'"
      :selected-item-ids="[lineItemId]"
      :base-ext-ref-num="lineItem.ext_ref_num"
      @onSubmit="getLineItem()"
    />
    <VCard>
      <VContainer fluid>
        <VOverlay
          :model-value="!!lineItemLoading"
          absolute
          class="align-center justify-center loading w-100 h-100"
          contained
          persistent
        >
          <VProgressCircular color="primary" indeterminate />
        </VOverlay>
        <VRow>
          <VCol class="py-0 d-flex align-center justify-space-between flex-wrap">
            <div class="d-flex justify-start align-center flex-wrap">
              <VBtn
                v-if="!isReviewDialog"
                color="light"
                size="small"
                class="px-0 text-decoration-none text-high-emphasis"
                :to="getLineItemListLink()"
              >
                <VIcon
                  start
                  icon="mdi-arrow-left"
                />
                Back
              </VBtn>
              <div>
                <h3 class="ml-2">
                  Artwork Review
                </h3>
              </div>
              <div
                v-if="!isReviewDialog"
              >
                <VBtn
                  size="small"
                  class="px-2 ma-1"
                  color="secondary"
                  @click="previousLineItem"
                >
                  <VIcon
                    icon="mdi-arrow-left"
                  />
                  Previous
                </VBtn>
                <VBtn
                  size="small"
                  class="px-2 ma-1"
                  color="secondary"
                  @click="nextLineItem"
                >
                  Next
                  <VIcon
                    icon="mdi-arrow-right"
                  />
                </VBtn>
              </div>
              <!-- Approve/Reject when not Supplier Production Partner -->
              <div
                v-if="!userStore.isSupplier || userStore.isSupplierProjectManagement"
                class="d-flex align-center flex-wrap"
              >
                <template v-if="!isActiveCampaign">
                  <VAlert
                    show
                    variant="tonal"
                    color="warning"
                    class="text-body-1 py-1 max-width-200 position-relative text-center cursor-default ml-2"
                  >
                    Inactive Project Item
                    <VIcon
                      size="small"
                      icon="tabler-info-circle"
                    />
                    <VMenu
                      activator="parent"
                      location="top center"
                      open-on-hover
                      open-delay="100"
                      close-delay="0"
                      offset="5"
                      class="default-tooltip"
                    >
                      <span class="px-2 text-center">
                        No changes allowed
                      </span>
                    </VMenu>
                  </VAlert>
                </template>
                <template v-else-if="lineItem.is_can_user_approve">
                  <template v-if="!lineItem.is_on_hold">
                    <div class="position-relative">
                      <VMenu
                        v-if="isDisabledApproveBtn"
                        activator="parent"
                        location="top center"
                        open-on-hover
                        open-delay="100"
                        close-delay="0"
                        offset="5"
                        class="default-tooltip"
                      >
                        <span class="px-2">
                          Annotations not resolved
                        </span>
                      </VMenu>
                      <VBtn
                        size="small"
                        class="px-2 ma-1"
                        color="success"
                        :disabled="isDisabledApproveBtn"
                        @click="approveLineItem"
                      >
                        <VIcon
                          icon="tabler-check"
                        />
                        Approve
                        <VProgressCircular
                          v-if="!!approveBtnLoading"
                          indeterminate
                          size="16"
                          width="2"
                          class="ml-1"
                          color="light"
                        />
                      </VBtn>
                    </div>
                    <VBtn
                      v-if="lineItem?.is_released"
                      :disabled="!!rejectBtnLoading"
                      size="small"
                      class="mt-1 mt-sm-0"
                      @click="rejectLineItem(null)"
                    >
                      <VIcon
                        icon="tabler-x"
                      />
                      Reject
                      <VProgressCircular
                        v-if="!!rejectBtnLoading"
                        indeterminate
                        size="16"
                        width="2"
                        class="ml-1"
                        color="light"
                      />
                    </VBtn>
                    <VMenu
                      v-else
                      location="bottom center"
                      open-on-click
                      @update:modelValue="handleRejectMenuOpen"
                    >
                      <template #activator="{ props }">
                        <VBtn
                          :disabled="!!rejectBtnLoading"
                          size="small"
                          class="px-2 ma-1"
                          v-bind="props"
                        >
                          <VIcon
                            icon="tabler-x"
                          />
                          Reject
                          <VProgressCircular
                            v-if="!!rejectBtnLoading"
                            indeterminate
                            size="16"
                            width="2"
                            class="ml-1"
                            color="light"
                          />
                        </VBtn>
                      </template>
                      <VCard class="position-relative reject-previous-statuses-menu-block">
                        <VOverlay
                          v-model="previousStatusListLoading"
                          persistent
                          absolute
                          contained
                          class="align-center justify-center"
                        >
                          <VProgressCircular
                            indeterminate
                            size="16"
                            width="2"
                            color="primary"
                          />
                        </VOverlay>
                        <div
                          v-if="!previousStatusList.length && !previousStatusListLoading"
                          class="px-2 py-3"
                        >
                          Previous statuses not found
                        </div>
                        <VList
                          v-else
                          class="px-1"
                          :class="{'pa-5': previousStatusListLoading}"
                          nav
                        >
                          <div
                            v-if="previousStatusList.length"
                            class="text-body-2 text-center no-pointer-events"
                          >
                            Back to:
                          </div>
                          <template
                            v-for="(status, statusKey) in previousStatusList"
                            :key="statusKey"
                          >
                            <VListItem
                              :title="status?.name"
                              class="border-t rounded-t-0"
                              @click="rejectLineItem(status?.id)"
                            />
                          </template>
                        </VList>
                      </VCard>
                    </VMenu>
                  </template>
                  <VAlert
                    v-else
                    show
                    variant="tonal"
                    color="warning"
                    class="text-body-2 py-1"
                  >
                    Items flagged as "On Hold"<br>cannot be Approved/Rejected
                  </VAlert>
                </template>
              </div>
            </div>
            <div class="line-item-review-toolbar-actions d-flex align-center flex-wrap">
              <VSelect
                v-model="artworkComparisonSelect"
                :items="[...artworkComparisons]"
                item-title="label"
                item-value="value"
                density="compact"
                class="mr-2"
              />
              <VBtn
                size="small"
                class="my-1 mr-2"
                color="secondary"
                append-icon="mdi-menu"
                @click="showAnnotationsSidebar = true"
              >
                Annotations
              </VBtn>
              <VBtn
                v-if="!isReviewDialog && isFileUploadSupported"
                :disabled="!isFileUploadReady"
                class="ma-1"
                color="primary"
                variant="flat"
                size="small"
                @click="openFilesUploadDialog"
              >
                <VIcon start icon="tabler-upload" />
                Upload Files
              </VBtn>
            </div>
          </VCol>
        </VRow>
        <VRow v-if="!isLinkCreatedUser">
          <VCol class="py-1">
            <div
              class="d-flex align-center flex-wrap"
            >
              <VBtn
                size="small"
                variant="elevated"
                density="compact"
                icon="tabler-history-toggle"
                class="rounded-lg ml-1"
                @click="showLineItemStatusHistoryDialog = true"
              >
                <VIcon icon="tabler-history-toggle" />
                <VMenu
                  activator="parent"
                  location="top center"
                  open-on-hover
                  open-delay="100"
                  close-delay="0"
                  offset="5"
                  class="default-tooltip"
                >
                  <span>
                    Show item status history
                  </span>
                </VMenu>
              </VBtn>
              <VBtn
                size="small"
                variant="elevated"
                density="compact"
                icon="tabler-bubble-text"
                class="rounded-lg ml-1"
                @click="showGlobalChatDialog = true"
              >
                <VIcon icon="tabler-bubble-text" />
                <VMenu
                  activator="parent"
                  location="top center"
                  open-on-hover
                  open-delay="100"
                  close-delay="0"
                  offset="5"
                  class="default-tooltip"
                >
                  <span>
                    Show item chat
                  </span>
                </VMenu>
              </VBtn>
            </div>
          </VCol>
        </VRow>
        <VRow>
          <VCol
            class="py-1"
            cols="12"
            sm="5"
            md="4"
          >
            {{ lineItem.description }}
            <template v-if="can('view', 'finances')">
              <div class="pt-2">
                <b>Total Price:</b> {{ lineItem.total }}
              </div>
            </template>
          </VCol>
          <VCol
            class="py-1 d-flex align-center justify-start"
            cols="12"
            sm="4"
            md="3"
          >
            <template v-if="lineItem?.responsible_list?.length">
              <table>
                <tr
                  v-for="(responsible, responsibleKey) in lineItem?.responsible_list"
                  :key="responsibleKey"
                >
                  <td
                    v-if="responsibleKey === 0"
                    :rowspan="lineItem?.responsible_list?.length"
                  >
                    <div class="d-flex flex-column">
                      Responsible:
                      <VBtn
                        v-if="lineItem?.approve_phases?.length"
                        color="light"
                        size="x-small"
                        class="px-1"
                      >
                        <VMenu
                          activator="parent"
                          location="top center"
                          open-on-hover
                          open-delay="100"
                          close-delay="0"
                          offset="5"
                          class="default-tooltip text-body-2"
                        >
                          <VDataTableVirtual
                            :headers="[
                              { key: 'responsible_approver_name', title: 'Approver', sortable: false },
                              { key: 'is_approved', title: 'Is Approved', sortable: false },
                            ]"
                            :items="[...lineItem?.approve_phases]"
                            no-data-text="Approve Info not found"
                            hover
                            density="compact"
                          >
                            <template #item.is_approved="{ item }">
                              <span v-if="item.raw.is_approved === true">
                                <VIcon
                                  color="success"
                                  icon="tabler-check"
                                />
                                by
                                {{ item.raw.actual_approver_name }}
                              </span>
                              <span v-else-if="item.raw.is_approved === false">
                                <VIcon
                                  color="primary"
                                  icon="tabler-x"
                                />
                                by
                                {{ item.raw.actual_approver_name }}
                              </span>
                              <span v-else>
                                <VIcon
                                  color="secondary"
                                  icon="tabler-minus"
                                />
                              </span>
                            </template>
                          </VDataTableVirtual>
                        </VMenu>
                        <VIcon
                          size="22"
                          icon="tabler-info-circle"
                        />
                      </VBtn>
                    </div>
                  </td>
                  <td class="pl-1">
                    {{ getResponsibleDisplayName(responsible) }}
                  </td>
                </tr>
              </table>
            </template>
            <template v-else>
              <span>
                Responsible:
                {{ lineItem?.responsible_name }}
              </span>
            </template>
          </VCol>
          <VCol
            class="py-1 d-flex align-center justify-start"
            cols="12"
            sm="4"
            md="3"
          >
            Assigned To: {{ lineItem.assigned_to_name }}
          </VCol>
          <VCol
            class="py-1 d-flex align-center justify-start"
            cols="12"
          >
            <b>Ext Ref #:</b>
            <span class="text-pre-wrap">
              {{ lineItem?.ext_ref_num || '' }}
            </span>
            <VBtn
              v-if="!isLinkCreatedUser"
              size="small"
              variant="tonal"
              density="compact"
              icon="tabler-pencil"
              class="rounded-lg ml-1"
              @click="showItemExtRefNumDialog = true"
            >
              <VIcon icon="tabler-pencil" />
              <VTooltip
                activator="parent"
                location="top"
                offset="5"
                class="default-tooltip"
              >
                <span>
                  Edit Ext Ref #
                </span>
              </VTooltip>
            </VBtn>
          </VCol>
        </VRow>
        <VRow class="mt-4">
          <VCol
            class="d-flex align-center justify-start"
            cols="12"
            sm="4"
            md="3"
          >
            <VBtn
              v-if="isCanAddAnnotation"
              icon="tabler-info-circle"
              size="small"
              variant="elevated"
              density="compact"
              class="rounded-sm mr-2"
              color="info"
            >
              <VIcon
                size="22"
                icon="tabler-info-circle"
              />
              <VTooltip
                activator="parent"
                location="top"
                offset="5"
                class="default-tooltip"
              >
                <span>
                  To add an annotation right-click on the PDF
                </span>
              </VTooltip>
            </VBtn>
            Line No: {{ lineItem.line_no }}
          </VCol>
          <VCol
            class="d-flex align-center justify-start"
            cols="12"
            sm="4"
            md="3"
          >
            <span class="mr-1">Status name:</span>
            <span
              :class="{'rejected-back-status-color': !!lineItem?.is_rejected_back_status}"
            >
              {{ lineItem.item_status_info ? lineItem.item_status_info.line_item_status_name : '' }}
            </span>
          </VCol>
          <VCol
            class="d-flex align-center justify-start"
            cols="12"
            sm="4"
            md="3"
          >
            <span class="mr-1">On Hold:</span>
            <VChip
              :color="lineItem.is_on_hold ? 'warning' : 'secondary'"
            >
              {{ lineItem.is_on_hold ? 'Yes' : 'No' }}
            </VChip>
          </VCol>
          <VCol
            class="d-flex align-center py-0"
            cols="12"
            sm="12"
            md="3"
          >
            <VSlider
              v-model="pdfScale"
              :max="500"
              :min="100"
              :step="10"
              label="Scale, %"
              hide-details
              class="pdf-scale-slider"
            >
              <template v-slot:append>
                <VTextField
                  v-model="pdfScale"
                  type="number"
                  style="width: 80px"
                  density="compact"
                  hide-details
                  variant="outlined"
                  class="pa-1"
                />
              </template>
            </VSlider>
          </VCol>
        </VRow>
        <VRow class="mt-0">
          <VCol
            class="d-flex align-center justify-start position-relative line-item-download-row"
            cols="12"
            sm="6"
            md=""
          >
            <span class="mr-1">
              Artwork:
            </span>
            <a
              target="_blank"
              :href="artworkFileUrl"
              :class="{ 'disabled-link': !artworkFileUrl }"
              @click.prevent="downloadSingleFile(artworkFileUrl, 'artwork')"
            >
              {{ artworkDownloadInfo.label }}
              <VOverlay
                :model-value="!!singleDownloadLoading[artworkFileUrl]"
                persistent
                absolute
                contained
                class="align-center justify-center"
              >
                <VProgressCircular size="16" width="2" indeterminate />
              </VOverlay>
              <VMenu
                v-if="artworkDownloadInfo.hasFiles"
                activator="parent"
                location="top center"
                open-on-hover
                open-on-focus
                open-delay="100"
                close-delay="0"
                offset="5"
                class="default-tooltip"
              >
                <span class="px-2">
                  {{ artworkDownloadInfo.tooltipLine }}
                </span>
              </VMenu>
            </a>
            <span
              v-if="artworkDownloadInfo.fileNames.length"
              class="ml-1 d-inline-flex flex-column line-item-download-file-list"
            >
              <span
                v-for="fileName in artworkDownloadInfo.fileNames"
                :key="`artwork-${fileName}`"
              >
                {{ fileName }}
              </span>
            </span>
          </VCol>
          <VCol
            class="d-flex align-center justify-start position-relative line-item-download-row"
            cols="12"
            sm="6"
            md=""
          >
            <span class="mr-1">
              Prepress Proof:
            </span>
            <a
              target="_blank"
              :href="prePressProofUrl"
              :class="{ 'disabled-link': !prePressProofUrl }"
              @click.prevent="downloadSingleFile(prePressProofUrl, 'prepress_proof')"
            >
              {{ prePressProofDownloadInfo.label }}
              <VOverlay
                :model-value="!!singleDownloadLoading[prePressProofUrl]"
                persistent
                absolute
                contained
                class="align-center justify-center"
              >
                <VProgressCircular size="16" width="2" indeterminate />
              </VOverlay>
              <VMenu
                v-if="prePressProofDownloadInfo.hasFiles"
                activator="parent"
                location="top center"
                open-on-hover
                open-on-focus
                open-delay="100"
                close-delay="0"
                offset="5"
                class="default-tooltip"
              >
                <span class="px-2">
                  {{ prePressProofDownloadInfo.tooltipLine }}
                </span>
              </VMenu>
            </a>
            <span
              v-if="prePressProofDownloadInfo.fileNames.length"
              class="ml-1 d-inline-flex flex-column line-item-download-file-list"
            >
              <span
                v-for="fileName in prePressProofDownloadInfo.fileNames"
                :key="`prepress-proof-${fileName}`"
              >
                {{ fileName }}
              </span>
            </span>
          </VCol>
          <VCol
            class="d-flex align-center justify-start position-relative line-item-download-row"
            cols="12"
            sm="6"
            md=""
          >
            <span class="mr-1">
              Scope PDF:
            </span>
            <a
              target="_blank"
              :href="scopeUrl"
              :class="{ 'disabled-link': !scopeUrl }"
              @click.prevent="downloadSingleFile(scopeUrl, 'scope')"
            >
              {{ scopeDownloadInfo.label }}
              <VOverlay
                :model-value="!!singleDownloadLoading[scopeUrl]"
                persistent
                absolute
                contained
                class="align-center justify-center"
              >
                <VProgressCircular size="16" width="2" indeterminate />
              </VOverlay>
              <VMenu
                v-if="scopeDownloadInfo.hasFiles"
                activator="parent"
                location="top center"
                open-on-hover
                open-on-focus
                open-delay="100"
                close-delay="0"
                offset="5"
                class="default-tooltip"
              >
                <span class="px-2">
                  {{ scopeDownloadInfo.tooltipLine }}
                </span>
              </VMenu>
            </a>
            <span
              v-if="scopeDownloadInfo.fileNames.length"
              class="ml-1 d-inline-flex flex-column line-item-download-file-list"
            >
              <span
                v-for="fileName in scopeDownloadInfo.fileNames"
                :key="`scope-${fileName}`"
              >
                {{ fileName }}
              </span>
            </span>
          </VCol>
          <VCol
            class="d-flex align-center justify-start position-relative line-item-download-row"
            cols="12"
            sm="6"
            md=""
          >
            <span class="mr-1">
              Prod AI:
            </span>
            <a
              target="_blank"
              :href="prodAiUrl"
              :class="{ 'disabled-link': !prodAiUrl }"
            >
              Download
              <VOverlay
                :model-value="!!singleDownloadLoading[prodAiUrl]"
                persistent
                absolute
                contained
                class="align-center justify-center"
              >
                <VProgressCircular size="16" width="2" indeterminate />
              </VOverlay>
            </a>
          </VCol>
          <VCol
            class="d-flex align-center justify-start position-relative line-item-download-row"
            cols="12"
            sm="6"
            md=""
          >
            <span class="mr-1">
              All Images:
            </span>
            <a
              :class="{ 'disabled-link': isDisabledDownloadAllLink }"
              :href="isDisabledDownloadAllLink ? null : '#'"
              @click.prevent="downloadAllFiles()"
            >
              {{ allImagesDownloadInfo.label }}
              <VOverlay
                :model-value="!!downloadAllFilesLoading"
                persistent
                absolute
                contained
                class="align-center justify-center"
              >
                <VProgressCircular
                  size="16"
                  width="2"
                  indeterminate
                />
              </VOverlay>
              <VMenu
                v-if="allImagesDownloadInfo.hasFiles"
                activator="parent"
                location="top center"
                open-on-hover
                open-on-focus
                open-delay="100"
                close-delay="0"
                offset="5"
                class="default-tooltip"
              >
                <span class="px-2 py-1 d-inline-block">
                  <span
                    v-for="line in allImagesDownloadInfo.tooltipLines"
                    :key="line"
                    class="d-block"
                  >
                    {{ line }}
                  </span>
                </span>
              </VMenu>
            </a>
          </VCol>
        </VRow>
        <VRow
          ref="artworksBlock"
          class="artwork-row mx-0"
        >
          <VCol
            v-show="[0, 1].includes(artworkComparisonSelect)"
            class="position-relative py-0 px-1"
            :class="{ 'min-height-200': artworkFileLoading }"
          >
            <div
              v-if="artworkFileUrl"
              style="margin: auto; max-height: inherit"
            >
              <VOverlay
                :model-value="artworkFileLoading"
                persistent
                absolute
                contained
                class="align-center justify-center"
              >
                <VProgressCircular
                  indeterminate
                  color="primary"
                />
              </VOverlay>
              <div class="text-center">
                <VBtn
                  size="sm"
                  class="p-0 rounded-circle"
                  pill
                  color="light"
                  :disabled="artworkFileCurrentPage === 1"
                  @click="artworkFileCurrentPage--"
                >
                  <VIcon icon="mdi-arrow-left-bold-circle-outline" />
                </VBtn>
                <span>
                    {{ artworkFileCurrentPage }}
                  </span>
                <VBtn
                  size="sm"
                  class="p-0 rounded-circle"
                  pill
                  color="light"
                  :disabled="artworkFileCurrentPage === artworkFilePageCount"
                  @click="artworkFileCurrentPage++"
                >
                  <VIcon icon="mdi-arrow-right-bold-circle-outline" />
                </VBtn>
              </div>
              <div
                id="artwork-file-pdf-container-id"
                class="artwork-file-pdf-container"
                @mousedown="onMoveMapStart($event, artworkFileMapPos, 'artwork-file-pdf-container-id')"
                v-on="{ mousemove: (event) => { if (isMoving) onMoveMap(event, false, artworkFileMapPos, 'artwork-file-pdf-container-id') } }"
                @mouseup="isMoving = false"
                @mouseout="isMoving = false"
              >
                <div
                  class="artwork-file-pdf-wrapper overflow-hidden"
                  :style="{ width: `${pdfScale}%` }"
                  @click.right.prevent="(event) => { createAnnotation(event) }"
                >
                  <VuePdfEmbed
                    v-if="artworkFileUrl"
                    ref="ArtworkFilePdf"
                    id="artwork-file-pdf"
                    textLayer
                    :source="artworkFileUrl"
                    :page="artworkFileCurrentPage"
                    @rendered="artworkFileLoading = false; artworkFilePageCount = $refs.ArtworkFilePdf.pageCount"
                  />
                  <div
                    v-else-if="!(artworkFileLoading)"
                    class="pa-2 text-center"
                  >
                    Failed to load PDF preview
                  </div>
                  <div
                    v-for="(annotation, key) in annotations.filter(annot => annot.page === artworkFileCurrentPage)"
                    :id="`annotation${key}`"
                    :key="key"
                    class="pdfAnnotation"
                    :class="{ 'hovered': annotation.hover, 'd-none': artworkFileLoading }"
                    :style="{ left: annotation.left + '%', top: annotation.top + '%' }"
                    annot="true"
                    @mouseover="setHover(annotation, true)"
                    @mouseout="setHover(annotation, false)"
                  >
                    <span annot="true">
                      {{ annotation.index }}
                    </span>
                    <VMenu
                      v-model="annotation.isShownAnnotationMenu"
                      :id="'pop' + annotation.id"
                      :activator="`#annotation${key}`"
                      :open-on-click="true"
                      :open-on-hover="false"
                      :close-on-content-click="false"
                      location="top"
                      offset="5"
                    >
                      <VCard>
                        <VCardText
                          class="border rounded-lg pa-3"
                        >
                          <VTextarea
                            v-model="annotation.comment"
                            placeholder="Enter comments..."
                            class="mb-1"
                            rows="3"
                            :disabled="annotation.is_resolved || !can('change', 'brandingartworkannot')"
                            :readonly="isLinkCreatedUser"
                          />
                          <div
                            v-if="!annotation.is_resolved && isActiveCampaign && !isLinkCreatedUser"
                            class="mt-1 d-flex"
                          >
                            <div class="save-btn">
                              <VBtn
                                v-if="can('change', 'brandingartworkannot')"
                                color="success"
                                :disabled="!annotation.comment"
                                @click="updateAnnotation(annotation)"
                              >
                                Save
                              </VBtn>
                            </div>
                            <VTooltip
                              v-if="!annotation.comment"
                              activator=".save-btn"
                              location="top"
                              offset="5"
                              class="default-tooltip"
                            >
                              <span>
                                Please, add comment
                              </span>
                            </VTooltip>
                            <div class="set-resolved-btn">
                              <VBtn
                                v-if="can('resolve', 'brandingartworkannot')"
                                class="ml-1"
                                color="info"
                                :disabled="!annotation.comment"
                                @click="annotation.is_resolved=true; updateAnnotation(annotation)"
                              >
                                Set Resolved
                              </VBtn>
                            </div>
                            <VTooltip
                              v-if="!annotation.comment"
                              activator=".set-resolved-btn"
                              location="top"
                              offset="5"
                              class="default-tooltip"
                            >
                              <span>
                                Please, add comment
                              </span>
                            </VTooltip>
                            <VBtn
                              v-if="can('delete', 'brandingartworkannot')"
                              class="ml-1"
                              @click="deleteAnnotation(annotation.id, key)"
                            >
                              Delete
                            </VBtn>
                          </div>
                        </VCardText>
                      </VCard>
                    </VMenu>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else-if="!artworkFileUrl"
              style="margin: auto;"
            >
              <h4>File not provided</h4>
            </div>
          </VCol>
          <VCol
            v-show="[0, 2].includes(artworkComparisonSelect)"
            class="position-relative py-0 px-1"
            :class="{ 'min-height-200': prePressProofLoading }"
          >
            <div
              v-if="prePressProofUrl"
              style="margin: auto; max-height: inherit"
            >
              <VOverlay
                :model-value="prePressProofLoading"
                persistent
                absolute
                contained
                class="align-center justify-center"
              >
                <VProgressCircular
                  indeterminate
                  color="primary"
                />
              </VOverlay>
              <div class="text-center">
                <VBtn
                  size="sm"
                  class="p-0 rounded-circle"
                  pill
                  color="light"
                  :disabled="prePressProofCurrentPage === 1"
                  @click="prePressProofCurrentPage--"
                >
                  <VIcon icon="mdi-arrow-left-bold-circle-outline" />
                </VBtn>
                <span>
                    {{ prePressProofCurrentPage }}
                  </span>
                <VBtn
                  size="sm"
                  class="p-0 rounded-circle"
                  pill
                  color="light"
                  :disabled="prePressProofCurrentPage === prePressProofPageCount"
                  @click="prePressProofCurrentPage++"
                >
                  <VIcon icon="mdi-arrow-right-bold-circle-outline" />
                </VBtn>
              </div>
              <div
                id="prepress-proof-pdf-container-id"
                class="artwork-pdf-container"
                @mousedown="onMoveMapStart($event, prePressProofMapPos, 'prepress-proof-pdf-container-id')"
                v-on="{ mousemove: (event) => { if (isMoving) onMoveMap(event, false, prePressProofMapPos, 'prepress-proof-pdf-container-id') } }"
                @mouseup="isMoving = false"
                @mouseout="isMoving = false"
              >
                <div
                  class="artwork-pdf-wrapper overflow-hidden"
                  :style="{ width: `${pdfScale}%` }"
                >
                  <VuePdfEmbed
                    v-if="prePressProofUrl"
                    ref="PrePressProofPdf"
                    id="prepress-proof"
                    :source="prePressProofUrl"
                    :page="prePressProofCurrentPage"
                    @rendered="prePressProofLoading = false; prePressProofPageCount = $refs.PrePressProofPdf.pageCount"
                  />
                  <div
                    v-else-if="!(prePressProofLoading)"
                    class="pa-2 text-center"
                  >
                    Failed to load PDF preview
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else-if="!prePressProofUrl"
              style="margin: auto;"
            >
              <h4>File not provided</h4>
            </div>
          </VCol>
          <VCol
            v-show="[1, 2].includes(artworkComparisonSelect)"
            class="position-relative py-0 px-1"
            :class="{ 'min-height-200': scopeLoading }"
          >
            <div
              v-if="scopeUrl"
              style="margin: auto; max-height: inherit"
            >
              <VOverlay
                :model-value="scopeLoading"
                persistent
                absolute
                contained
                class="align-center justify-center"
              >
                <VProgressCircular
                  indeterminate
                  color="primary"
                />
              </VOverlay>
              <div class="text-center">
                <VBtn
                  size="sm"
                  class="p-0 rounded-circle"
                  pill
                  color="light"
                  :disabled="scopeCurrentPage === 1"
                  @click="scopeCurrentPage--"
                >
                  <VIcon icon="mdi-arrow-left-bold-circle-outline" />
                </VBtn>
                <span>
                    {{ scopeCurrentPage }}
                  </span>
                <VBtn
                  size="sm"
                  class="p-0 rounded-circle"
                  pill
                  color="light"
                  :disabled="scopeCurrentPage === scopePageCount"
                  @click="scopeCurrentPage++"
                >
                  <VIcon icon="mdi-arrow-right-bold-circle-outline" />
                </VBtn>
              </div>
              <div
                id="scope-pdf-container-id"
                class="artwork-pdf-container"
                @mousedown="onMoveMapStart($event, scopeMapPos, 'scope-pdf-container-id')"
                v-on="{ mousemove: (event) => { if (isMoving) onMoveMap(event, false, scopeMapPos, 'scope-pdf-container-id') } }"
                @mouseup="isMoving = false"
                @mouseout="isMoving = false"
              >
                <div
                  class="artwork-pdf-wrapper overflow-hidden"
                  :style="{ width: `${pdfScale}%` }"
                >
                  <VuePdfEmbed
                    v-if="scopeUrl"
                    ref="ScopePdf"
                    id="scope-pdf"
                    :source="scopeUrl"
                    :page="scopeCurrentPage"
                    @rendered="scopeLoading = false; scopePageCount = $refs.ScopePdf.pageCount"
                  />
                  <div
                    v-else-if="!(scopeLoading)"
                    class="pa-2 text-center"
                  >
                    Failed to load PDF preview
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else-if="!scopeUrl"
              style="margin: auto;"
            >
              <h4>File not provided</h4>
            </div>
          </VCol>
        </VRow>
      </VContainer>
    </VCard>
  </div>
</template>

<style lang="scss">
.line-item-review-toolbar-actions {
  max-width: 100%;
  min-width: 0;
  gap: 4px;
}

.artwork-file-pdf-container {
  overflow: scroll;
  max-width: 100%;
  max-height: inherit;
  .artwork-file-pdf-wrapper {
    position: relative;
    margin: 0 auto;
    &:active {
      cursor: all-scroll;
    }
    .pdfAnnotation {
      position: absolute;
      display: block;
      width: 24px;
      height: 24px;
      background: #dd082b;
      border-radius: 50%;
      text-align: center;
      color: #fff;
      cursor: pointer;
      &.hovered {
        background: #fff;
        color: #dd082b;
        border: 1px solid #dd082b;
      }
    }
  }
}

.artwork-pdf-container {
  overflow: scroll;
  max-width: 100%;
  max-height: inherit;
  .artwork-pdf-wrapper {
    position: relative;
    margin: 0 auto;
    &:active {
      cursor: all-scroll;
    }
  }
}

canvas {
  width: 100% !important;
  height: unset !important;
}

.artwork-row {
  position: relative;
  > .v-col {
    max-height: inherit;
  }
}

.pdf-scale-slider {
  input {
    padding: 0 5px !important;
  }
}

.line-item-download-row {
  font-size: 0.82rem;
  padding-left: 5px;
  padding-right: 5px;
}

.line-item-download-file-list {
  font-size: 0.7rem;
  line-height: 1.35;
  word-break: break-word;
}

a {
  color: rgb(var(--v-theme-secondary));
  cursor: not-allowed;
  &:link {
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
}

.reject-previous-statuses-menu-block {
  box-shadow: 0 3px 17px -10px #5e5e5e !important;
}

.rejected-back-status-color {
  color: rgb(var(--v-theme-primary));
}

a.disabled-link {
  pointer-events: none;
  color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
  cursor: not-allowed;
}
</style>
